import { ReactNode } from 'react';
import { DateTime } from 'luxon';
import { RenderIf } from 'react-rainbow-components';
import Badge from 'components/Badge';
import UserAvatar from 'components/UserAvatar';
import DetailElement from 'components/CustomerProfile/detail';
import Phone from 'components/icons/phone';
import DeviceMobile from 'components/icons/deviceMobile';
import Home from 'components/icons/home';
import Building from 'components/icons/building';
import MailClosed from 'components/icons/mailClosed';
import Languages from 'components/icons/languages';
import MapPin from 'components/icons/mapPin';
import IdCard from 'components/icons/idcard';
import Document from 'components/icons/document';
import Clipboard from 'components/icons/clipboard';
import Dollar from 'components/icons/dollar';
import User from 'components/icons/user';
import Users from 'components/icons/users';
import formatIntlPhoneNumber from 'data/services/phone/formatter';
import { NexhealthPatient } from '../types';
import {
    DetailsGrid,
    PatientBadges,
    PatientHeader,
    PatientInfo,
    PatientMeta,
    PatientName,
    Section,
    SectionHeader,
    SectionTitle,
} from './styled';

const Demographics = ({
    patient,
    actions,
    compact = false,
}: {
    patient: NexhealthPatient;
    actions?: ReactNode;
    compact?: boolean;
}) => {
    const { bio } = patient;
    const name = patient.name || [patient.first_name, patient.last_name].filter(Boolean).join(' ');
    const initials = [patient.first_name, patient.last_name]
        .map((value) => value?.[0])
        .filter(Boolean)
        .join('')
        .toUpperCase();

    const dob = bio?.date_of_birth ? DateTime.fromISO(bio.date_of_birth) : undefined;
    const meta = [
        dob?.isValid && `${Math.floor(Math.abs(dob.diffNow('years').years))} yrs`,
        bio?.gender,
        dob?.isValid && `Born ${dob.toLocaleString(DateTime.DATE_MED)}`,
    ].filter(Boolean).join(' · ');
    const createdAt = patient.created_at ? DateTime.fromISO(patient.created_at) : undefined;

    const address = [
        bio?.address_line_1,
        bio?.address_line_2,
        [bio?.city, bio?.state].filter(Boolean).join(', '),
        bio?.zip_code,
    ].filter(Boolean).join(' ');

    return (
        <>
            <PatientHeader>
                <UserAvatar size="large" value={String(patient.id)} initials={initials} />
                <PatientInfo>
                    <PatientName>{name}</PatientName>
                    <RenderIf isTrue={meta}>
                        <PatientMeta>{meta}</PatientMeta>
                    </RenderIf>
                    <RenderIf isTrue={compact}>
                        <PatientBadges>
                            <RenderIf isTrue={patient.inactive}>
                                <Badge label="Inactive" variant="default" size="small" />
                            </RenderIf>
                            <RenderIf isTrue={createdAt?.isValid}>
                                <Badge
                                    label={`Added ${createdAt?.toLocaleString(DateTime.DATE_MED)}`}
                                    variant="brand"
                                    size="small"
                                />
                            </RenderIf>
                            {actions}
                        </PatientBadges>
                    </RenderIf>
                </PatientInfo>
                <RenderIf isTrue={!compact}>
                    <PatientBadges>
                        <RenderIf isTrue={patient.inactive}>
                            <Badge label="Inactive" variant="default" />
                        </RenderIf>
                        <RenderIf isTrue={createdAt?.isValid}>
                            <Badge
                                label={`Added ${createdAt?.toLocaleString(DateTime.DATE_MED)}`}
                                variant="brand"
                            />
                        </RenderIf>
                        {actions}
                    </PatientBadges>
                </RenderIf>
            </PatientHeader>
            <Section>
                <SectionHeader>
                    <SectionTitle>Demographics</SectionTitle>
                </SectionHeader>
                <DetailsGrid>
                    <DetailElement
                        icon={<DeviceMobile />}
                        label="Mobile"
                        value={formatIntlPhoneNumber(bio?.cell_phone_number)}
                    />
                    <DetailElement
                        icon={<Home />}
                        label="Home"
                        value={formatIntlPhoneNumber(bio?.home_phone_number)}
                    />
                    <DetailElement
                        icon={<Building />}
                        label="Work"
                        value={formatIntlPhoneNumber(bio?.work_phone_number)}
                    />
                    <DetailElement
                        icon={<Phone />}
                        label="Phone"
                        value={formatIntlPhoneNumber(bio?.phone_number)}
                    />
                    <DetailElement
                        icon={<MailClosed />}
                        label="Email"
                        value={bio?.email || patient.email}
                    />
                    <DetailElement
                        icon={<Languages />}
                        label="Language"
                        value={patient.preferred_language}
                    />
                    <DetailElement icon={<MapPin />} label="Address" value={address} />
                    <DetailElement icon={<IdCard />} label="Patient ID" value={patient.id} />
                    <DetailElement icon={<Document />} label="Chart ID" value={patient.chart_id} />
                    <DetailElement icon={<Clipboard />} label="EHR ID" value={patient.foreign_id} />
                    <DetailElement icon={<Dollar />} label="Billing" value={patient.billing_type} />
                    <DetailElement icon={<Users />} label="Guarantor" value={patient.guarantor_id} />
                    <DetailElement icon={<User />} label="Provider" value={patient.provider_id} />
                </DetailsGrid>
            </Section>
        </>
    );
};

export default Demographics;
